/** Reading status of a book */
export type ReadingStatus = "want-to-read" | "reading" | "read";

/** Star rating: 1-5 */
export type StarRating = 1 | 2 | 3 | 4 | 5;

/** A recommendation from someone */
export interface Recommendation {
  /** Name of the person who recommended the book */
  from: string;
  /** What they said about it */
  comment: string;
}

/** A book entry in the library */
export interface Book {
  /** Unique identifier (UUID) */
  id: string;
  /** Book title */
  title: string;
  /** Author name(s) */
  author: string;
  /** Date published (year or full date string) */
  datePublished: string;
  /** Series name, if part of a series */
  series?: string;
  /** Book number in the series (string to support "1.1", "2a", etc.) */
  seriesNumber?: string;
  /** ISBN number */
  isbn?: string;
  /** Current reading status */
  status: ReadingStatus;
  /** Star rating (1-5), only set if status is 'read' */
  rating?: StarRating;
  /** Recommendations from others */
  recommendations: Recommendation[];
  /** Date added to the list (ISO string) */
  dateAdded: string;
  /** Date started reading (ISO date string) */
  dateStarted?: string;
  /** Date finished reading (ISO date string) */
  dateRead?: string;
  /** Personal notes */
  notes?: string;
  /** Cover image URL (e.g., Open Library Covers API) */
  coverUrl?: string;
}

/** The full library data stored in JSON */
export interface BookLibrary {
  /** Schema version for future migrations */
  version: number;
  /** Last modified ISO timestamp */
  lastModified: string;
  /** All books */
  books: Book[];
}

/** Rating label descriptions */
export const RATING_LABELS: Record<StarRating, string> = {
  5: "All-time favorite",
  4: "Loved it",
  3: "Enjoyable",
  2: "Meh",
  1: "Terrible",
};

/** Status display labels */
export const STATUS_LABELS: Record<ReadingStatus, string> = {
  "want-to-read": "Want to Read",
  reading: "Reading",
  read: "Read",
};
